import React, { useEffect, useMemo, useState, useRef } from "react";
import {
    Box,
    Typography,
    Paper,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Divider,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Card,
    CardContent,
    FormLabel,
    Snackbar,
    SnackbarContent,
    useMediaQuery,
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import SendIcon from "@material-ui/icons/Send";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import NoteService from "./Locality/Service/noteService";
import StudentService from "./Locality/Service/studentService";
import SocketService from "./Locality/Service/socketService";
import AddClassService from "./Locality/Service/addClassService";
import SectionService from "./Locality/Service/sectionService";
import { useFormik } from "formik";
import * as Yup from "yup";

/** Relative last-seen label from presence.lastSeenAt (e.g. "1 hour ago"). */
const formatLastSeenLabel = (lastSeenAt) => {
    if (!lastSeenAt) return "Not available";
    const seen = new Date(lastSeenAt);
    if (Number.isNaN(seen.getTime())) return "Not available";

    const diffMs = Math.max(0, Date.now() - seen.getTime());
    const minutes = Math.floor(diffMs / 60000);
    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;

    const days = Math.floor(hours / 24);
    if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;

    const months = Math.floor(days / 30);
    if (months < 12) return `${months} month${months === 1 ? "" : "s"} ago`;

    const years = Math.floor(days / 365);
    return `${years} year${years === 1 ? "" : "s"} ago`;
};

/** Local calendar day key (YYYY-MM-DD) for WhatsApp-style separators. */
const getChatDayKey = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

/** WhatsApp-style day label: Today / Yesterday / full date. */
const formatChatDayLabel = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return '';

    const today = new Date();
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const startOfMsg = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const diffDays = Math.round((startOfToday - startOfMsg) / 86400000);

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    return d.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: d.getFullYear() !== today.getFullYear() ? 'numeric' : undefined,
    });
};

/** Status meanings shown in the student list legend. */
const PRESENCE_STATUS_MEANINGS = {
    // online: {
    //     label: 'Online',
    //     meaning: 'Parent is currently active on the mobile app.',
    //     color: '#2e7d32',
    // },
    unavailable: {
        label: 'Not available',
        meaning: 'Parent app is not connected or presence data is unavailable.',
        color: '#9e9e9e',
    },
    // offline: {
    //     label: 'Last seen',
    //     meaning: 'Parent was active recently but is offline now.',
    //     color: '#ed6c02',
    // },
};

/** Statuses may be hidden from the legend map, so never index it directly. */
const getPresenceStatusLabel = (status, fallback) =>
    PRESENCE_STATUS_MEANINGS[status]?.label || fallback;

const getPresenceStatusMeaning = (status) =>
    PRESENCE_STATUS_MEANINGS[status]?.meaning || '';

/**
 * presence null → Not available; isOnline → Online; else Last seen {time}.
 * Returns text, online flag, status key, and meaning for legend/tooltips.
 */
const getStudentPresenceDisplay = (student) => {
    const presence = student?.presence;
    if (presence == null) {
        return {
            text: getPresenceStatusLabel('unavailable', 'Not available'),
            online: false,
            status: 'unavailable',
            meaning: getPresenceStatusMeaning('unavailable'),
        };
    }
    if (presence.isOnline) {
        return {
            text: getPresenceStatusLabel('online', 'Online'),
            online: true,
            status: 'online',
            meaning: getPresenceStatusMeaning('online'),
        };
    }
    const lastSeen = formatLastSeenLabel(presence.lastSeenAt);
    if (lastSeen === "Not available") {
        return {
            text: getPresenceStatusLabel('unavailable', 'Not available'),
            online: false,
            status: 'unavailable',
            meaning: getPresenceStatusMeaning('unavailable'),
        };
    }
    return {
        text: `Last seen ${lastSeen}`,
        online: false,
        status: 'offline',
        meaning: getPresenceStatusMeaning('offline'),
    };
};

/** Convenience label used in student list rows. */
const getPresenceLabel = (student) => getStudentPresenceDisplay(student).text;

function PresenceStatusLegend() {
    const classes = useStyles();
    return (
        <Box className={classes.presenceLegend}>
            <Typography className={classes.presenceLegendTitle}>Status meanings</Typography>
            {Object.entries(PRESENCE_STATUS_MEANINGS).map(([key, item]) => (
                <Box key={key} className={classes.presenceLegendRow}>
                    <span
                        className={classes.presenceLegendDot}
                        style={{ backgroundColor: item.color }}
                        aria-hidden
                    />
                    <Box>
                        <Typography className={classes.presenceLegendLabel}>{item.label}</Typography>
                        <Typography className={classes.presenceLegendMeaning}>{item.meaning}</Typography>
                    </Box>
                </Box>
            ))}
        </Box>
    );
}

const SCHOOL_ROLES = ['SCHOOLE', 'TEACHER', 'SUPERADMIN'];

const normalizeId = (value) => {
    if (value == null || value === '') return '';
    if (typeof value === 'string' || typeof value === 'number') {
        const s = String(value).trim();
        return s === '[object Object]' ? '' : s;
    }
    if (typeof value === 'object') {
        if (value.$oid) return String(value.$oid);
        if (value._id && value._id !== value) return normalizeId(value._id);
        if (value.id && value.id !== value) return normalizeId(value.id);
        if (typeof value.toHexString === 'function') return value.toHexString();
        const s = typeof value.toString === 'function' ? value.toString() : '';
        if (s && s !== '[object Object]') return s;
    }
    return '';
};

const getSenderRole = (msg) => {
    const direct = String(msg?.senderRole || msg?.createdBy?.role || '').toUpperCase();
    if (direct) return direct;
    const createdBy = msg?.createdBy;
    if (createdBy && typeof createdBy === 'object') {
        if (Array.isArray(createdBy.students) && createdBy.students.length > 0) return 'PARENT';
        if (createdBy.teacher && (createdBy.teacher._id || createdBy.teacher.teacherName)) return 'TEACHER';
        if (createdBy.school && (createdBy.school._id || createdBy.school.schooleName)) return 'SCHOOLE';
    }
    return '';
};

const isOwnMessage = (msg, userDetails) => {
    const myRole = String(userDetails?.role || '').toUpperCase();
    const senderRole = getSenderRole(msg);
    const iAmSchool = SCHOOL_ROLES.includes(myRole);
    const senderIsSchool = SCHOOL_ROLES.includes(senderRole);
    const senderIsParent = senderRole === 'PARENT';

    if (myRole === 'PARENT' && (senderIsParent || senderIsSchool)) {
        return senderIsParent;
    }
    if (iAmSchool && (senderIsParent || senderIsSchool)) {
        return senderIsSchool;
    }

    const me = normalizeId(userDetails?._id) || normalizeId(userDetails?.id);
    const senderId = normalizeId(msg?.senderId) || normalizeId(msg?.createdBy);
    return Boolean(me && senderId && me === senderId);
};

const useStyles = makeStyles((theme) => ({
    yellowHeader: {
        backgroundColor: '#FFC107',
        color: 'white',
        padding: '16px 24px',
        marginBottom: 16,
        borderRadius: '4px 4px 0 0',
        flexShrink: 0,
        [theme.breakpoints.down('sm')]: {
            padding: '10px 14px',
            marginBottom: 10,
        },
    },
    headerTitle: {
        fontWeight: 'bold',
        color: 'white',
        [theme.breakpoints.down('sm')]: {
            fontSize: '1.15rem',
        },
    },
    layout: {
        display: 'flex',
        gap: 16,
        alignItems: 'stretch',
        height: 'calc(100vh - 200px)',
        maxHeight: 'calc(100vh - 200px)',
        overflow: 'hidden',
        [theme.breakpoints.down('sm')]: {
            // Mobile shows one panel at a time (list OR chat), each full height.
            gap: 0,
            height: 'calc(100vh - 150px)',
            maxHeight: 'calc(100vh - 150px)',
        },
    },
    studentsPanel: {
        width: 300,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        height: '100%',
        minHeight: 0,
        [theme.breakpoints.down('sm')]: {
            width: '100%',
        },
    },
    chatHeader: {
        backgroundColor: '#f5f5f5',
        borderBottom: '1px solid #e0e0e0',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexShrink: 0,
        padding: 16,
        [theme.breakpoints.down('sm')]: {
            padding: '8px 10px',
        },
    },
    chatHeaderTitle: {
        fontWeight: 700,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        [theme.breakpoints.down('sm')]: {
            fontSize: '0.95rem',
        },
    },
    studentsPanelHeader: {
        flexShrink: 0,
    },
    studentsListScroll: {
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
    },
    notesPanel: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        minHeight: 0,
        height: '100%',
    },
    notesContainer: {
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        padding: 16,
        backgroundColor: '#ece5dd',
        display: 'flex',
        flexDirection: 'column',
        WebkitOverflowScrolling: 'touch',
        [theme.breakpoints.down('sm')]: {
            padding: 10,
        },
    },
    chatMessageRow: {
        display: 'flex',
        width: '100%',
        marginBottom: 8,
    },
    chatMessageRowSent: {
        justifyContent: 'flex-end',
    },
    chatMessageRowReceived: {
        justifyContent: 'flex-start',
    },
    chatMessageBubble: {
        padding: '8px 12px 6px',
        borderRadius: 8,
        maxWidth: '75%',
        wordWrap: 'break-word',
        [theme.breakpoints.down('xs')]: {
            maxWidth: '85%',
        },
        boxShadow: '0 1px 1px rgba(0,0,0,0.08)',
    },
    messageSent: {
        backgroundColor: '#dcf8c6',
        color: '#111',
        borderTopRightRadius: 0,
    },
    messageReceived: {
        backgroundColor: '#ffffff',
        color: '#111',
        borderTopLeftRadius: 0,
    },
    messageTime: {
        display: 'block',
        marginTop: 4,
        opacity: 0.65,
        fontSize: '0.68rem',
        textAlign: 'right',
    },
    dateSeparator: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        margin: '10px 0 14px',
        width: '100%',
    },
    dateSeparatorChip: {
        backgroundColor: '#ffffff',
        color: '#54656f',
        fontSize: '0.75rem',
        fontWeight: 600,
        padding: '5px 12px',
        borderRadius: 8,
        boxShadow: '0 1px 0.5px rgba(11, 20, 26, 0.13)',
        letterSpacing: 0.2,
    },
    chatComposer: {
        flexShrink: 0,
        padding: 12,
        borderTop: '1px solid #e0e0e0',
        display: 'flex',
        gap: 8,
        alignItems: 'center',
        backgroundColor: '#f0f0f0',
        [theme.breakpoints.down('sm')]: {
            padding: 8,
            gap: 4,
        },
    },
    studentRow: {
        position: 'relative',
        paddingRight: 40,
    },
    studentRowName: {
        display: 'block',
        fontSize: '0.95rem',
    },
    studentRowMeta: {
        display: 'block',
        fontSize: '0.75rem',
        color: '#666',
        marginTop: 2,
    },
    studentRowPresence: {
        display: 'block',
        fontSize: '0.7rem',
        color: '#888',
        marginTop: 2,
    },
    studentRowPresenceOnline: {
        color: '#2e7d32',
        fontWeight: 600,
    },
    studentRowPresenceUnavailable: {
        color: '#757575',
        fontWeight: 500,
    },
    studentRowPresenceOffline: {
        color: '#ed6c02',
        fontWeight: 500,
    },
    presenceStatusDot: {
        display: 'inline-block',
        width: 8,
        height: 8,
        borderRadius: '50%',
        marginRight: 6,
        flexShrink: 0,
        verticalAlign: 'middle',
    },
    presenceLegend: {
        marginTop: 12,
        padding: '10px 12px',
        borderRadius: 8,
        backgroundColor: '#f8fafc',
        border: '1px solid #e8edf3',
    },
    presenceLegendTitle: {
        fontSize: '0.72rem',
        fontWeight: 700,
        color: '#475569',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        marginBottom: 8,
    },
    presenceLegendRow: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 8,
        marginBottom: 8,
        '&:last-child': {
            marginBottom: 0,
        },
    },
    presenceLegendDot: {
        width: 8,
        height: 8,
        borderRadius: '50%',
        marginTop: 5,
        flexShrink: 0,
    },
    presenceLegendLabel: {
        fontSize: '0.75rem',
        fontWeight: 700,
        color: '#334155',
        lineHeight: 1.3,
    },
    presenceLegendMeaning: {
        fontSize: '0.7rem',
        color: '#64748b',
        lineHeight: 1.35,
    },
    studentRowDot: {
        position: 'absolute',
        right: 8,
        top: '50%',
        transform: 'translateY(-50%)',
        minWidth: 22,
        height: 22,
        padding: '0 7px',
        borderRadius: 11,
        backgroundColor: '#e53935',
        color: 'white',
        fontSize: '0.75rem',
        fontWeight: 700,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    sectionTitle: {
        color: '#FFC107',
        fontWeight: 'bold',
        marginBottom: '12px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    yellowButton: {
        backgroundColor: '#FFC107',
        color: 'white',
        padding: '16px',
        fontSize: '18px',
        fontWeight: 'bold',
        textTransform: 'uppercase',
        width: '100%',
        marginTop: '8px',
        '&:hover': {
            backgroundColor: '#FFB300',
        },
    },
    composeCard: {
        padding: 8,
        boxShadow: 'none',
    },
    clickableBadge: {
        cursor: 'pointer',
        display: 'inline-flex',
        borderRadius: 8,
        padding: 4,
        transition: 'background-color 0.15s ease',
        '&:hover': {
            backgroundColor: 'rgba(15, 59, 98, 0.08)',
        },
    },
    clickableBadgeLight: {
        cursor: 'pointer',
        display: 'inline-flex',
        borderRadius: 8,
        padding: 4,
        transition: 'background-color 0.15s ease',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
        },
    },
    unreadPanelHint: {
        fontSize: '0.78rem',
        color: '#64748b',
        marginTop: 4,
    },
}));

const SCHOOL_NOTIFICATION_SOUND = `${process.env.PUBLIC_URL || ''}/sound/universfield-new-notification-051-494246.mp3`;

export default function NoteList() {
    const classes = useStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    // Mobile: show the student list or the open chat, one at a time (WhatsApp-style).
    const [mobileShowChat, setMobileShowChat] = useState(false);
    const userDetails = useMemo(() => JSON.parse(localStorage.getItem("userDetail") || "{}"), []);

    const [studentsList, setStudentsList] = useState([]);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [notesList, setNotesList] = useState([]);
    const [chatMessages, setChatMessages] = useState([]);
    const [chatInput, setChatInput] = useState('');
    const [unreadCounts, setUnreadCounts] = useState({}); // { noteId: count }
    const [studentUnreadCounts, setStudentUnreadCounts] = useState({}); // { studentId: totalCount }
    const totalSchoolChatUnread = useMemo(
        () => Object.values(unreadCounts).reduce((a, v) => a + (Number(v) || 0), 0),
        [unreadCounts],
    );

    const formatNewCount = (count) => {
        const n = Number(count) || 0;
        return n > 99 ? "99+" : String(n);
    };
    const [classesList, setClassesList] = useState([]);
    const [sectionsList, setSectionsList] = useState([]);
    const [filterClassId, setFilterClassId] = useState("");
    const [filterSectionId, setFilterSectionId] = useState("");
    const [composeOpen, setComposeOpen] = useState(false);
    const [composeStudent, setComposeStudent] = useState(null);
    const [toast, setToast] = useState({ open: false, type: 'success', message: '' });
    const socketInitializedRef = useRef(false);
    const chatMessagesEndRef = useRef(null);
    const groupedDataMapRef = useRef(new Map()); // Store grouped notes data
    const groupedStudentDetailsRef = useRef(new Map()); // studentId -> student summary
    const selectedStudentRef = useRef(null);
    const activeRootNoteIdRef = useRef(null);
    /** noteId -> studentId. Used to attribute unread bumps to the right student row. */
    const noteToStudentRef = useRef(new Map());
    const unreadCountsRef = useRef({});
    const studentUnreadCountsRef = useRef({});
    const loadNotesRequestRef = useRef(0);
    const notificationAudioRef = useRef(null);
    const [showUnreadStudentsOnly, setShowUnreadStudentsOnly] = useState(false);

    const isSchooleRole = useMemo(
        () => String(userDetails?.role || '').toUpperCase() === 'SCHOOLE',
        [userDetails?.role],
    );

    useEffect(() => {
        if (!isSchooleRole) return;
        notificationAudioRef.current = new Audio(SCHOOL_NOTIFICATION_SOUND);
        notificationAudioRef.current.preload = 'auto';
    }, [isSchooleRole]);

    const playSchoolNotificationSound = () => {
        if (!isSchooleRole) return;
        try {
            const audio = notificationAudioRef.current;
            if (!audio) return;
            audio.currentTime = 0;
            const playPromise = audio.play();
            if (playPromise && typeof playPromise.catch === 'function') {
                playPromise.catch(() => {});
            }
        } catch (err) {
            // Browser may block autoplay until user interacts with the page
        }
    };

    useEffect(() => {
        unreadCountsRef.current = unreadCounts;
    }, [unreadCounts]);
    useEffect(() => {
        studentUnreadCountsRef.current = studentUnreadCounts;
    }, [studentUnreadCounts]);

    useEffect(() => {
        selectedStudentRef.current = selectedStudent;
    }, [selectedStudent]);

    const canComposeForStudent = () => {
        const r = String(userDetails?.role || "").toUpperCase();
        return r === "SCHOOLE" || r === "TEACHER" || r === "SUPERADMIN";
    };

    const applyUnreadSummaryData = (res) => {
        const data = res?.data || res || {};
        const normalized = {};
        Object.entries(data.byNoteId || {}).forEach(([noteId, count]) => {
            const n = Number(count) || 0;
            if (n > 0) normalized[String(noteId)] = n;
        });
        setUnreadCounts(normalized);
        setStudentUnreadCounts(data.byStudentId || {});
        const noteStudentMap = data.noteIdToStudentId || {};
        Object.entries(noteStudentMap).forEach(([noteId, studentId]) => {
            if (noteId && studentId) {
                noteToStudentRef.current.set(String(noteId), String(studentId));
            }
        });
    };

    const clearUnreadForNote = (noteId) => {
        const id = String(noteId);
        setUnreadCounts((prev) => {
            const next = { ...prev };
            delete next[id];
            return next;
        });
    };

    const persistNoteRead = async (noteId) => {
        if (!noteId) return;
        try {
            await NoteService.markNoteRead(String(noteId));
        } catch (err) {
            console.error('Error marking note as read:', err);
        }
    };

    const handleUnreadUpdate = (payload) => {
        const noteId = payload?.noteId ? String(payload.noteId) : '';
        if (!noteId) return;

        const payloadStudentId = payload?.studentId
            ? String(payload.studentId)
            : noteToStudentRef.current.get(noteId);
        const viewingSameStudent =
            selectedStudentRef.current?._id &&
            payloadStudentId &&
            String(selectedStudentRef.current._id) === payloadStudentId;

        if (viewingSameStudent) {
            persistNoteRead(noteId);
            // Keep the open conversation live when a new message arrives for this student
            if (payloadStudentId) {
                loadMergedChat(payloadStudentId, { markRead: true });
            }
            return;
        }

        const studentId = payloadStudentId;
        if (studentId) {
            noteToStudentRef.current.set(noteId, String(studentId));
        }

        if (payload.unreadCount != null) {
            const count = Number(payload.unreadCount) || 0;
            setUnreadCounts((prev) => {
                const next = { ...prev };
                if (count <= 0) delete next[noteId];
                else next[noteId] = count;
                return next;
            });
            if (count > 0) playSchoolNotificationSound();
            return;
        }

        const inc = Number(payload.increment) || 1;
        setUnreadCounts((prev) => ({
            ...prev,
            [noteId]: (Number(prev[noteId]) || 0) + inc,
        }));
        playSchoolNotificationSound();
    };

    const handleNewNote = (payload) => {
        if (!payload?.noteId || !payload?.studentId) return;
        const senderRole = String(payload.senderRole || '').toUpperCase();
        if (senderRole !== 'PARENT') return;

        const sid = String(payload.studentId);
        const noteCard = {
            _id: payload.noteId,
            noteType: payload.noteType,
            note: payload.note,
            createdAt: payload.createdAt,
            messageKind: payload.messageKind || 'NOTE',
            studentId: { _id: sid, firstName: payload.studentName },
        };
        mergeCreatedNoteIntoCache(sid, noteCard);
        noteToStudentRef.current.set(String(payload.noteId), sid);

        if (payload.studentName) {
            groupedStudentDetailsRef.current.set(sid, {
                _id: sid,
                firstName: payload.studentName.split(' ')[0] || payload.studentName,
                lastName: payload.studentName.split(' ').slice(1).join(' ') || '',
            });
        }

        if (selectedStudentRef.current?._id && String(selectedStudentRef.current._id) === sid) {
            loadMergedChat(sid, { markRead: true });
        }

        const studentLabel = payload.studentName || 'Student';
        const preview = payload.preview || payload.noteType || 'New message';
        setToast({
            open: true,
            type: 'success',
            message: `${studentLabel}: ${preview}`,
        });
    };

    const openComposeForStudent = (student) => {
        if (!student?._id || !canComposeForStudent()) return;
        setComposeStudent(student);
        setSelectedStudent(student);
        setComposeOpen(true);
    };

    const mergeCreatedNoteIntoCache = (studentId, createdNote) => {
        if (!studentId || !createdNote?._id) return;
        const sid = String(studentId);
        const prev = groupedDataMapRef.current.get(sid) || [];
        const withoutDup = prev.filter((n) => String(n._id) !== String(createdNote._id));
        const next = [createdNote, ...withoutDup];
        groupedDataMapRef.current.set(sid, next);
        // Keep the noteId -> studentId map current so unread bumps land on the right row.
        noteToStudentRef.current.set(String(createdNote._id), sid);
        if (selectedStudent?._id && String(selectedStudent._id) === sid) {
            setNotesList(next);
        }
    };

    const composeFormik = useFormik({
        initialValues: { note: '' },
        enableReinitialize: true,
        validationSchema: Yup.object().shape({
            note: Yup.string().trim().required('Please enter a message'),
        }),
        onSubmit: async (values, { resetForm }) => {
            const student = composeStudent || selectedStudent;
            if (!student?._id) return;
            try {
                const classRef = student.classId;
                const classId = typeof classRef === 'object' ? classRef?._id : classRef;
                const schoolRef = student.schooleId || userDetails?.schooleId || userDetails?.schoolId;
                const schooleId = typeof schoolRef === 'object' ? schoolRef?._id : schoolRef;

                const body = {
                    messageKind: 'NOTE',
                    noteType: 'Note',
                    note: values.note || '',
                    studentId: student._id,
                };
                if (classId) body.classId = classId;
                if (schooleId) body.schooleId = schooleId;

                const res = await NoteService.createNote(body);
                const createdNote = res?.data || res;
                if (createdNote?._id) {
                    mergeCreatedNoteIntoCache(student._id, createdNote);
                }
                resetForm();
                setComposeOpen(false);
                setComposeStudent(null);
                setToast({ open: true, type: 'success', message: 'Message sent successfully.' });
                await loadMergedChat(student._id, { markRead: true });
            } catch (err) {
                setToast({
                    open: true,
                    type: 'error',
                    message: err.response?.data?.message || err?.message || 'Error sending message',
                });
            }
        },
    });

    useEffect(() => {
        loadStudents();

        // Initialize socket connection
        if (!socketInitializedRef.current) {
            SocketService.initializeSocket();
            socketInitializedRef.current = true;
        }

        // Seed unread counts ONCE from a single aggregated endpoint.
        NoteService.getUnreadSummary()
            .then(applyUnreadSummaryData)
            .catch((err) => console.error('Error fetching unread summary:', err));

        const role = String(userDetails?.role || '').toUpperCase();
        if (['SCHOOLE', 'TEACHER', 'SUPERADMIN'].includes(role)) {
            SocketService.joinSchoolNoteFeed();
        }

        const offUpdate = SocketService.onUnreadUpdate(handleUnreadUpdate);

        const offCleared = SocketService.onUnreadCleared((payload) => {
            if (!payload?.noteId) return;
            clearUnreadForNote(payload.noteId);
        });

        const offNewNote = SocketService.onNewNote(handleNewNote);

        return () => {
            if (typeof offUpdate === 'function') offUpdate();
            if (typeof offCleared === 'function') offCleared();
            if (typeof offNewNote === 'function') offNewNote();
            if (activeRootNoteIdRef.current) {
                SocketService.leaveNoteChat(activeRootNoteIdRef.current);
                activeRootNoteIdRef.current = null;
            }
            socketInitializedRef.current = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (selectedStudent?._id) {
            loadMergedChat(selectedStudent._id, { markRead: true });
        } else {
            leaveActiveChatRoom();
            setChatMessages([]);
            setNotesList([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedStudent]);

    useEffect(() => {
        const role = String(userDetails?.role || "").toUpperCase();
        const schooleId = userDetails?.schooleId || userDetails?.schoolId;
        if (!(role === "SCHOOLE" || role === "SUPERADMIN") || !schooleId) return;
        AddClassService.getAllAddClass(schooleId, "SCHOOLE")
            .then((res) => setClassesList(Array.isArray(res) ? res : res?.data || []))
            .catch(() => setClassesList([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userDetails]);

    useEffect(() => {
        const role = String(userDetails?.role || "").toUpperCase();
        const schooleId = userDetails?.schooleId || userDetails?.schoolId;
        if (!(role === "SCHOOLE" || role === "SUPERADMIN") || !schooleId) return;
        if (!filterClassId) {
            setSectionsList([]);
            setFilterSectionId("");
            return;
        }
        SectionService.findSectionList({ schooleId, classId: filterClassId, status: true })
            .then((res) => {
                const data = res?.data ?? res;
                setSectionsList(Array.isArray(data) ? data : []);
            })
            .catch(() => setSectionsList([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterClassId]);

    const loadStudents = async () => {
        try {
            const schooleId = userDetails?.schooleId || userDetails?.schoolId;
            if (!schooleId) return;

            const role = String(userDetails?.role || "").toUpperCase();

            const groupedData = (await NoteService.getNotesGroupedByStudent()) || [];

            const groupedDataMap = new Map();
            const noteToStudent = new Map();
            groupedData.forEach((group) => {
                const studentId = group.studentDetails.studentId?._id || group.studentDetails.studentId;
                if (studentId) {
                    const sid = String(studentId);
                    const notes = group.studentNotes || [];
                    const firstNote = notes[0] || null;
                    groupedDataMap.set(sid, notes);
                    groupedStudentDetailsRef.current.set(sid, {
                        _id: studentId,
                        firstName: group.studentDetails.firstName,
                        lastName: group.studentDetails.lastName,
                        classId: group.studentDetails.classId || firstNote?.classId || null,
                        sectionId: firstNote?.sectionId || null,
                    });
                    notes.forEach((n) => {
                        if (n?._id) noteToStudent.set(String(n._id), sid);
                    });
                }
            });
            groupedDataMapRef.current = groupedDataMap;
            noteToStudentRef.current = noteToStudent;

            if (role === "SCHOOLE" || role === "SUPERADMIN") {
                try {
                    const allStudentsRes = await StudentService.findStudentList({ schooleId });
                    const allStudents = allStudentsRes?.data || allStudentsRes || [];
                    const list = Array.isArray(allStudents) ? allStudents : [];
                    setStudentsList(list);
                    if (list.length > 0 && !selectedStudent && !showUnreadStudentsOnly) {
                        setSelectedStudent(list[0]);
                    }
                } catch (e) {
                    setStudentsList([]);
                }
                return;
            }

            if (groupedData.length === 0) {
                setStudentsList([]);
                return;
            }

            const studentsWithMessages = groupedData.map((group) => {
                const studentId = group.studentDetails.studentId?._id || group.studentDetails.studentId;
                const firstNote = group.studentNotes && group.studentNotes.length > 0 ? group.studentNotes[0] : null;
                return {
                    _id: studentId,
                    firstName: group.studentDetails.firstName,
                    lastName: group.studentDetails.lastName,
                    classId: firstNote?.classId || null,
                    notes: group.studentNotes || [],
                };
            });

            setStudentsList(studentsWithMessages);
            if (studentsWithMessages.length > 0 && !selectedStudent && !showUnreadStudentsOnly) {
                setSelectedStudent(studentsWithMessages[0]);
            }
        } catch (err) {
            console.error('Error fetching students:', err);
            setStudentsList([]);
        }
    };

    const getStudentClassId = (student) => {
        const c = student?.classId;
        if (!c) return "";
        return typeof c === "object" ? String(c._id || "") : String(c);
    };

    const getStudentSectionId = (student) => {
        const s = student?.sectionId;
        if (!s) return "";
        return typeof s === "object" ? String(s._id || "") : String(s);
    };

    const getStudentUnreadCount = (studentId) => {
        const sid = String(studentId);
        let total = 0;
        Object.entries(unreadCounts).forEach(([noteId, count]) => {
            if (noteToStudentRef.current.get(String(noteId)) === sid) {
                total += Number(count) || 0;
            }
        });
        return total;
    };

    const resolveStudentRecord = (studentId) => {
        const sid = String(studentId);
        const fromList = (studentsList || []).find((item) => String(item?._id) === sid);
        if (fromList) return fromList;

        const fromGrouped = groupedStudentDetailsRef.current.get(sid);
        if (fromGrouped) return fromGrouped;

        return { _id: studentId, firstName: "Student", lastName: "" };
    };

    const unreadStudents = useMemo(() => {
        const totalsByStudent = new Map();

        Object.entries(unreadCounts).forEach(([noteId, count]) => {
            if (Number(count) <= 0) return;
            const studentId = noteToStudentRef.current.get(String(noteId));
            if (!studentId) return;
            const sid = String(studentId);
            totalsByStudent.set(sid, (totalsByStudent.get(sid) || 0) + Number(count));
        });

        return Array.from(totalsByStudent.entries())
            .filter(([, total]) => total > 0)
            .sort((a, b) => b[1] - a[1])
            .map(([studentId]) => resolveStudentRecord(studentId));
        // noteToStudentRef is a ref; unreadCounts drive recomputation
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [studentsList, unreadCounts]);

    const unreadStudentCount = unreadStudents.length;

    const handleShowUnreadStudents = () => {
        if (totalSchoolChatUnread <= 0 || unreadStudentCount <= 0) return;
        setShowUnreadStudentsOnly(true);
        setFilterClassId("");
        setFilterSectionId("");
        setSelectedStudent(unreadStudents[0]);
    };

    const renderNewMessagesChip = () => {
        if (showUnreadStudentsOnly || totalSchoolChatUnread <= 0) return null;
        return (
            <Chip
                size="small"
                label={`${formatNewCount(totalSchoolChatUnread)} new`}
                onClick={handleShowUnreadStudents}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleShowUnreadStudents();
                    }
                }}
                role="button"
                tabIndex={0}
                aria-label="View students with new messages"
                style={{
                    backgroundColor: "#e53935",
                    color: "#fff",
                    fontWeight: 700,
                    height: 22,
                    cursor: "pointer",
                }}
            />
        );
    };

    /** School admin: show student list only after class is chosen, and section when class has sections */
    const adminStudentListVisible = useMemo(() => {
        const role = String(userDetails?.role || "").toUpperCase();
        if (!(role === "SCHOOLE" || role === "SUPERADMIN")) return true;
        if (!filterClassId) return false;
        if (sectionsList.length > 0 && !filterSectionId) return false;
        return true;
    }, [userDetails, filterClassId, filterSectionId, sectionsList.length]);

    const filteredStudents = useMemo(() => {
        const role = String(userDetails?.role || "").toUpperCase();
        if ((role === "SCHOOLE" || role === "SUPERADMIN") && !adminStudentListVisible) {
            return [];
        }
        if (!(role === "SCHOOLE" || role === "SUPERADMIN")) return studentsList;
        return (studentsList || []).filter((st) => {
            if (filterClassId && getStudentClassId(st) !== String(filterClassId)) return false;
            if (filterSectionId && getStudentSectionId(st) !== String(filterSectionId)) return false;
            return true;
        });
    }, [studentsList, userDetails, filterClassId, filterSectionId, adminStudentListVisible]);

    const displayStudents = useMemo(() => {
        if (showUnreadStudentsOnly) {
            const list = [...unreadStudents];
            if (selectedStudent?._id) {
                const selectedId = String(selectedStudent._id);
                if (!list.some((student) => String(student?._id) === selectedId)) {
                    list.unshift(selectedStudent);
                }
            }
            return list;
        }

        const role = String(userDetails?.role || "").toUpperCase();
        if ((role === "SCHOOLE" || role === "SUPERADMIN") && !adminStudentListVisible) {
            return selectedStudent?._id ? [selectedStudent] : [];
        }

        return filteredStudents;
    }, [showUnreadStudentsOnly, unreadStudents, filteredStudents, selectedStudent, adminStudentListVisible, userDetails?.role]);

    const isSchoolAdminRole = ["SCHOOLE", "SUPERADMIN"].includes(String(userDetails?.role || "").toUpperCase());
    const hideStudentPicker =
        isSchoolAdminRole &&
        !showUnreadStudentsOnly &&
        !adminStudentListVisible &&
        !selectedStudent?._id;

    const applyFiltersForSelectedStudent = () => {
        if (!selectedStudent?._id) return;
        const classId = getStudentClassId(selectedStudent);
        const sectionId = getStudentSectionId(selectedStudent);
        if (classId && !filterClassId) setFilterClassId(classId);
        if (sectionId && !filterSectionId) setFilterSectionId(sectionId);
    };

    const handleShowAllStudents = () => {
        applyFiltersForSelectedStudent();
        setShowUnreadStudentsOnly(false);
    };

    useEffect(() => {
        if (!showUnreadStudentsOnly || unreadStudents.length === 0) return;
        if (!selectedStudent?._id) {
            setSelectedStudent(unreadStudents[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showUnreadStudentsOnly, unreadStudents]);

    useEffect(() => {
        const role = String(userDetails?.role || "").toUpperCase();
        if (!(role === "SCHOOLE" || role === "SUPERADMIN")) return;
        if (showUnreadStudentsOnly) return;

        if (!filteredStudents || filteredStudents.length === 0) {
            // Keep the current student visible while class/section filters are unset.
            if (selectedStudent?._id) return;
            setSelectedStudent(null);
            setNotesList([]);
            return;
        }

        const currentId = selectedStudent?._id ? String(selectedStudent._id) : null;
        const stillExists = currentId
            ? filteredStudents.some((s) => String(s?._id) === currentId)
            : false;

        if (!stillExists) {
            setSelectedStudent(filteredStudents[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterClassId, filterSectionId, filteredStudents, showUnreadStudentsOnly]);

    const loadNotesForStudent = async (studentId) => {
        try {
            if (!studentId) return;

            const sid = String(studentId);
            const requestId = ++loadNotesRequestRef.current;
            let notes = [];

            const cached = groupedDataMapRef.current?.get(sid);
            if (Array.isArray(cached) && cached.length > 0) {
                notes = [...cached];
            } else {
                const schooleId = userDetails?.schooleId || userDetails?.schoolId;
                try {
                    const body = { studentId: sid };
                    if (schooleId) body.schooleId = schooleId;
                    const res = await NoteService.findNoteList(body);
                    notes = Array.isArray(res) ? res : res?.data || [];
                } catch {
                    const classId = userDetails?.role === 'TEACHER' ? userDetails?.classId : null;
                    const res = await NoteService.getAllNotes(classId);
                    notes = (res || []).filter((note) => {
                        const noteStudentId = note?.studentId?._id || note?.studentId;
                        return String(noteStudentId) === sid;
                    });
                }

                const byId = new Map(notes.map((n) => [String(n._id), n]));
                const unread = unreadCountsRef.current || {};
                const missingIds = Object.entries(unread)
                    .filter(([noteId, count]) => {
                        if (Number(count) <= 0) return false;
                        const mappedStudent = noteToStudentRef.current.get(String(noteId));
                        return mappedStudent === sid;
                    })
                    .map(([noteId]) => noteId)
                    .filter((id) => !byId.has(String(id)));

                if (missingIds.length > 0) {
                    const fetched = await Promise.all(
                        missingIds.map((id) =>
                            NoteService.getNoteById(id)
                                .then((r) => r?.data || r)
                                .catch(() => null)
                        )
                    );
                    fetched.forEach((n) => {
                        if (n?._id) byId.set(String(n._id), n);
                    });
                }

                notes = Array.from(byId.values()).sort(
                    (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
                );
                groupedDataMapRef.current.set(sid, notes);
            }

            if (requestId !== loadNotesRequestRef.current) return;

            notes.forEach((n) => {
                if (n?._id) noteToStudentRef.current.set(String(n._id), sid);
            });
            setNotesList(notes);
        } catch (err) {
            console.error('Error fetching notes:', err);
        }
    };

    const scrollChatToBottom = () => {
        setTimeout(() => {
            if (chatMessagesEndRef.current) {
                chatMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
            }
        }, 100);
    };

    const leaveActiveChatRoom = () => {
        if (activeRootNoteIdRef.current) {
            SocketService.leaveNoteChat(activeRootNoteIdRef.current);
            activeRootNoteIdRef.current = null;
        }
    };

    /** Prefer latest root NOTE; else any parentNoteId; else first message id. */
    const resolveRootNoteId = (messages) => {
        const list = Array.isArray(messages) ? messages : [];
        const roots = list.filter((m) => {
            const kind = String(m?.messageKind || 'NOTE').toUpperCase();
            return kind !== 'CHAT' && !m?.parentNoteId;
        });
        if (roots.length > 0) return String(roots[roots.length - 1]._id);

        for (let i = list.length - 1; i >= 0; i -= 1) {
            const parent = list[i]?.parentNoteId;
            if (parent) return String(parent._id || parent);
        }
        if (list.length > 0 && list[0]?._id) return String(list[0]._id);
        return null;
    };

    const appendChatMessage = (messageData) => {
        if (!messageData) return;
        setChatMessages((prev) => {
            const exists = prev.some((msg) => String(msg._id) === String(messageData._id));
            if (exists) return prev;
            return [...prev, messageData];
        });
        scrollChatToBottom();
    };

    const joinRootNoteRoom = (rootNoteId) => {
        if (!rootNoteId) return;
        if (activeRootNoteIdRef.current && activeRootNoteIdRef.current !== rootNoteId) {
            SocketService.leaveNoteChat(activeRootNoteIdRef.current);
        }
        activeRootNoteIdRef.current = rootNoteId;
        SocketService.joinNoteChat(rootNoteId, {
            onMessage: (messageData) => {
                const msgStudentId =
                    messageData?.studentId?._id || messageData?.studentId || messageData?.studentId;
                const currentId = selectedStudentRef.current?._id
                    ? String(selectedStudentRef.current._id)
                    : '';
                if (msgStudentId && currentId && String(msgStudentId) !== currentId) return;
                appendChatMessage(messageData);
            },
            onError: (error) => {
                console.error('Socket error:', error);
            },
        });
    };

    const loadMergedChat = async (studentId, { markRead = false } = {}) => {
        try {
            if (!studentId) return;
            const sid = String(studentId);
            const requestId = ++loadNotesRequestRef.current;

            const res = await NoteService.getChatMessages(sid);
            if (requestId !== loadNotesRequestRef.current) return;

            const messages = Array.isArray(res) ? res : res?.data || [];
            setChatMessages(messages);

            // Keep a notes cache for unread attribution / compose
            const roots = messages.filter((m) => {
                const kind = String(m?.messageKind || 'NOTE').toUpperCase();
                return kind !== 'CHAT' && !m?.parentNoteId;
            });
            const cacheNotes = roots.length > 0 ? [...roots].reverse() : [...messages].reverse();
            groupedDataMapRef.current.set(sid, cacheNotes);
            setNotesList(cacheNotes);
            cacheNotes.forEach((n) => {
                if (n?._id) noteToStudentRef.current.set(String(n._id), sid);
            });
            messages.forEach((n) => {
                const parent = n?.parentNoteId?._id || n?.parentNoteId;
                if (parent) noteToStudentRef.current.set(String(parent), sid);
                if (n?._id) noteToStudentRef.current.set(String(n._id), sid);
            });

            if (markRead) {
                const unreadIds = Object.entries(unreadCountsRef.current || {})
                    .filter(([noteId, count]) => {
                        if (Number(count) <= 0) return false;
                        return noteToStudentRef.current.get(String(noteId)) === sid;
                    })
                    .map(([noteId]) => noteId);
                const rootIds = roots.map((n) => n._id).filter(Boolean);
                await markNotesAsRead([...new Set([...unreadIds, ...rootIds.map(String)])]);
            }

            const rootId = resolveRootNoteId(messages);
            joinRootNoteRoom(rootId);
            scrollChatToBottom();
        } catch (err) {
            console.error('Error loading merged chat:', err);
            setChatMessages([]);
        }
    };

    const markNotesAsRead = async (noteIds) => {
        const ids = (noteIds || []).map((id) => String(id)).filter(Boolean);
        if (ids.length === 0) return;

        ids.forEach((noteId) => clearUnreadForNote(noteId));
        await Promise.all(ids.map((noteId) => persistNoteRead(noteId)));
    };

    const handleSelectStudent = (student) => {
        if (!student?._id) return;
        setSelectedStudent(student);
        setMobileShowChat(true);
    };

    const showListPanel = !isMobile || !mobileShowChat;
    const showChatPanel = !isMobile || mobileShowChat;

    const handleSendMessage = async () => {
        try {
            const text = (chatInput || '').trim();
            if (!text) return;
            const studentId = selectedStudent?._id;
            if (!studentId) return;

            setChatInput('');
            const rootId = resolveRootNoteId(chatMessages) || activeRootNoteIdRef.current;

            if (!rootId) {
                const classRef = selectedStudent.classId;
                const classId = typeof classRef === 'object' ? classRef?._id : classRef;
                const schoolRef = selectedStudent.schooleId || userDetails?.schooleId || userDetails?.schoolId;
                const schooleId = typeof schoolRef === 'object' ? schoolRef?._id : schoolRef;
                const body = {
                    studentId,
                    note: text,
                    noteType: 'Note',
                };
                if (classId) body.classId = classId;
                if (schooleId) body.schooleId = schooleId;
                const res = await NoteService.sendMergedChat(body);
                const createdNote = res?.data || res;
                if (createdNote?._id) {
                    mergeCreatedNoteIntoCache(studentId, createdNote);
                }
                await loadMergedChat(studentId, { markRead: true });
                return;
            }

            await sendViaRestAPI(studentId, text, rootId);
        } catch (err) {
            console.error('Error sending message:', err);
        }
    };

    const sendViaRestAPI = async (studentId, text, rootId) => {
        try {
            await NoteService.sendMergedChat({
                studentId,
                note: text,
                parentNoteId: rootId,
            });
            await loadMergedChat(studentId, { markRead: true });
        } catch (err) {
            console.error('Error sending via REST API:', err);
        }
    };

    const getStudentName = (student) => {
        if (!student) return '-';
        const first = student.firstName || '';
        const last = student.lastName || '';
        return `${first} ${last}`.trim() || '-';
    };

    const getStudentNameFromNote = (note) => {
        const s = note?.studentId;
        if (!s) return '-';
        const first = s.firstName || '';
        const last = s.lastName || '';
        return `${first} ${last}`.trim() || '-';
    };

    const getClassName = (student) => {
        return student?.classId?.className || '-';
    };

    const getClassNameFromNote = (note) => {
        return note?.studentId?.classId?.className || note?.classId?.className || '-';
    };

    const formatTime = (dateString) => {
        if (!dateString) return '';
        const d = new Date(dateString);
        if (Number.isNaN(d.getTime())) return '';
        return d.toLocaleString('en-US', { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatTimeShort = (dateString) => {
        if (!dateString) return '';
        const d = new Date(dateString);
        if (Number.isNaN(d.getTime())) return '';
        return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    };

    const isMine = (msg) => isOwnMessage(msg, userDetails);

    return (
        <>
            <div className={classes.yellowHeader}>
                <Typography variant="h5" className={classes.headerTitle}>
                    Messages from Parents
                </Typography>
            </div>

            <Box className={classes.layout}>
                    {/* Left Panel - Students List */}
                    <Paper
                        className={classes.studentsPanel}
                        style={{ display: showListPanel ? undefined : 'none' }}
                    >
                        <Box p={2} className={classes.studentsPanelHeader}>
                            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gridGap={8}>
                                <Box>
                                    <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                                        {showUnreadStudentsOnly ? "Unread Students" : "Students"}
                                    </Typography>
                                    {showUnreadStudentsOnly && (
                                        <Typography className={classes.unreadPanelHint}>
                                            {unreadStudentCount} student{unreadStudentCount === 1 ? "" : "s"} with new messages
                                        </Typography>
                                    )}
                                </Box>
                                {renderNewMessagesChip()}
                            </Box>
                            {showUnreadStudentsOnly && (
                                <Box mt={1.5}>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={handleShowAllStudents}
                                        style={{ textTransform: "none" }}
                                    >
                                        Show all students
                                    </Button>
                                </Box>
                            )}
                            {["SCHOOLE", "SUPERADMIN"].includes(String(userDetails?.role || "").toUpperCase()) &&
                                !showUnreadStudentsOnly && (
                                <Box mt={2} display="flex" flexDirection="column" gridGap={12}>
                                    <FormControl variant="outlined" fullWidth size="small">
                                        <InputLabel id="note-list-filter-class">Class</InputLabel>
                                        <Select
                                            labelId="note-list-filter-class"
                                            value={filterClassId}
                                            onChange={(e) => {
                                                setFilterClassId(e.target.value);
                                                setFilterSectionId("");
                                                setSectionsList([]);
                                                setShowUnreadStudentsOnly(false);
                                            }}
                                            label="Class"
                                        >
                                            <MenuItem value="">
                                                <em>All classes</em>
                                            </MenuItem>
                                            {classesList.map((c) => (
                                                <MenuItem key={c._id} value={c._id}>
                                                    {c.className}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    {filterClassId && sectionsList.length > 0 && (
                                        <FormControl variant="outlined" fullWidth size="small">
                                            <InputLabel id="note-list-filter-section">Section</InputLabel>
                                            <Select
                                                labelId="note-list-filter-section"
                                                value={filterSectionId}
                                                onChange={(e) => {
                                                    setFilterSectionId(e.target.value);
                                                    setShowUnreadStudentsOnly(false);
                                                }}
                                                label="Section"
                                            >
                                                <MenuItem value="">
                                                    <em>All sections</em>
                                                </MenuItem>
                                                {sectionsList.map((s) => (
                                                    <MenuItem key={s._id} value={s._id}>
                                                        {s.sectionName}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    )}
                                </Box>
                            )}
                            {isSchoolAdminRole && !showUnreadStudentsOnly && (
                                <PresenceStatusLegend />
                            )}
                        </Box>
                        {isSchoolAdminRole &&
                            !showUnreadStudentsOnly &&
                            hideStudentPicker && (
                                <Box px={2} pb={2}>
                                    <Typography variant="body2" style={{ color: "#777" }}>
                                        {!filterClassId
                                            ? "Select a class to view students."
                                            : "Select a section to view students."}
                                    </Typography>
                                </Box>
                            )}
                        <Divider
                            style={{
                                flexShrink: 0,
                                display: hideStudentPicker ? "none" : undefined,
                            }}
                        />
                        <Box
                            className={classes.studentsListScroll}
                            style={{
                                display: hideStudentPicker ? "none" : undefined,
                            }}
                        >
                            <List dense component="div" disablePadding>
                            {displayStudents.length === 0 ? (
                                <Box p={2}>
                                    <Typography variant="body2" style={{ color: '#777' }}>
                                        {showUnreadStudentsOnly
                                            ? "No new messages right now."
                                            : !["SCHOOLE", "SUPERADMIN"].includes(String(userDetails?.role || "").toUpperCase()) ||
                                              adminStudentListVisible
                                                ? studentsList.length === 0
                                                    ? "No students found."
                                                    : "No students match the selected class or section."
                                                : ""}
                                    </Typography>
                                </Box>
                            ) : (
                                displayStudents.map((student) => {
                                    const selected = selectedStudent?._id && student._id
                                        ? String(selectedStudent._id) === String(student._id)
                                        : false;
                                    const studentIdStr = student._id ? String(student._id) : String(student);
                                    const unreadCount = getStudentUnreadCount(studentIdStr);
                                    const hasUnread = unreadCount > 0;
                                    return (
                                        <React.Fragment key={student._id}>
                                            <ListItem
                                                button
                                                selected={selected}
                                                onClick={() => handleSelectStudent(student)}
                                                className={classes.studentRow}
                                            >
                                                <ListItemText
                                                    primary={
                                                        <Typography
                                                            className={classes.studentRowName}
                                                            style={{ fontWeight: hasUnread || selected ? 700 : 400 }}
                                                        >
                                                            {getStudentName(student)}
                                                        </Typography>
                                                    }
                                                    secondary={
                                                        (() => {
                                                            const presenceDisplay = isSchoolAdminRole
                                                                ? getStudentPresenceDisplay(student)
                                                                : null;
                                                            return (
                                                                <>
                                                                    <Typography className={classes.studentRowMeta}>
                                                                        {getClassName(student)}
                                                                    </Typography>
                                                                    {presenceDisplay && (
                                                                        <Typography
                                                                            className={`${classes.studentRowPresence}${
                                                                                presenceDisplay.status === 'online'
                                                                                    ? ` ${classes.studentRowPresenceOnline}`
                                                                                    : presenceDisplay.status === 'unavailable'
                                                                                        ? ` ${classes.studentRowPresenceUnavailable}`
                                                                                        : ` ${classes.studentRowPresenceOffline}`
                                                                            }`}
                                                                            title={presenceDisplay.meaning}
                                                                        >
                                                                            <span
                                                                                className={classes.presenceStatusDot}
                                                                                style={{
                                                                                    backgroundColor: PRESENCE_STATUS_MEANINGS[presenceDisplay.status]?.color || '#9e9e9e',
                                                                                }}
                                                                                aria-hidden
                                                                            />
                                                                            {presenceDisplay.text}
                                                                        </Typography>
                                                                    )}
                                                                </>
                                                            );
                                                        })()
                                                    }
                                                />
                                                {hasUnread && (
                                                    <span className={classes.studentRowDot} aria-label={`${unreadCount} new`}>
                                                        {formatNewCount(unreadCount)}
                                                    </span>
                                                )}
                                            </ListItem>
                                            <Divider />
                                        </React.Fragment>
                                    );
                                })
                            )}
                            </List>
                        </Box>
                    </Paper>

                    {/* Right Panel - Merged WhatsApp-style chat */}
                    <Paper
                        className={classes.notesPanel}
                        style={{ display: showChatPanel ? undefined : 'none' }}
                    >
                        <Box className={classes.chatHeader}>
                            {isMobile && (
                                <IconButton
                                    size="small"
                                    onClick={() => setMobileShowChat(false)}
                                    aria-label="Back to students"
                                >
                                    <ArrowBackIcon />
                                </IconButton>
                            )}
                            <Typography variant="subtitle1" className={classes.chatHeaderTitle}>
                                {selectedStudent
                                    ? `Chat with ${getStudentName(selectedStudent)}`
                                    : 'Select a student'}
                            </Typography>
                        </Box>
                        <Box className={classes.notesContainer}>
                            {!selectedStudent ? (
                                <Box style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                                    <Typography variant="body2">
                                        Please select a student from the left panel.
                                    </Typography>
                                </Box>
                            ) : chatMessages.length === 0 ? (
                                <Box style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                                    <Typography variant="body2">
                                        No messages yet. Start the conversation below.
                                    </Typography>
                                </Box>
                            ) : (
                                <>
                                    {chatMessages.map((m, idx) => {
                                        const isFormNote =
                                            m.noteType && (m.messageKind !== 'CHAT' || !m.messageKind);
                                        const mine = isMine(m);
                                        const dayKey = getChatDayKey(m.createdAt);
                                        const prevDayKey = idx > 0
                                            ? getChatDayKey(chatMessages[idx - 1]?.createdAt)
                                            : '';
                                        const showDaySeparator = Boolean(dayKey) && dayKey !== prevDayKey;
                                        return (
                                            <React.Fragment key={m._id || idx}>
                                                {showDaySeparator ? (
                                                    <Box className={classes.dateSeparator}>
                                                        <Typography
                                                            component="span"
                                                            className={classes.dateSeparatorChip}
                                                        >
                                                            {formatChatDayLabel(m.createdAt)}
                                                        </Typography>
                                                    </Box>
                                                ) : null}
                                                <Box
                                                    className={`${classes.chatMessageRow} ${
                                                        mine
                                                            ? classes.chatMessageRowSent
                                                            : classes.chatMessageRowReceived
                                                    }`}
                                                    style={{
                                                        display: 'flex',
                                                        width: '100%',
                                                        justifyContent: mine ? 'flex-end' : 'flex-start',
                                                        marginBottom: 8,
                                                    }}
                                                >
                                                    <Box
                                                        className={`${classes.chatMessageBubble} ${
                                                            mine ? classes.messageSent : classes.messageReceived
                                                        }`}
                                                        style={{
                                                            backgroundColor: isFormNote
                                                                ? '#fff3cd'
                                                                : mine
                                                                    ? '#dcf8c6'
                                                                    : '#ffffff',
                                                            color: isFormNote ? '#856404' : '#111',
                                                            border: isFormNote ? '1px solid #ffc107' : undefined,
                                                            borderTopRightRadius: mine ? 0 : 8,
                                                            borderTopLeftRadius: mine ? 8 : 0,
                                                        }}
                                                    >
                                                        {isFormNote && (
                                                            <Box
                                                                style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 8,
                                                                    marginBottom: 4,
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant="caption"
                                                                    style={{
                                                                        fontWeight: 700,
                                                                        fontSize: '0.7rem',
                                                                        opacity: 0.9,
                                                                    }}
                                                                >
                                                                    📋 {m.noteType}
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                        <Typography variant="body2">{m.note || '-'}</Typography>
                                                        <Typography
                                                            variant="caption"
                                                            className={classes.messageTime}
                                                        >
                                                            {formatTimeShort(m.createdAt)}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </React.Fragment>
                                        );
                                    })}
                                    <div ref={chatMessagesEndRef} />
                                </>
                            )}
                        </Box>
                        {selectedStudent && canComposeForStudent() && (
                            <Box className={classes.chatComposer}>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    placeholder="Type a message..."
                                    value={chatInput}
                                    onChange={(e) => setChatInput(e.target.value)}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter' && !e.shiftKey && chatInput.trim()) {
                                            e.preventDefault();
                                            handleSendMessage();
                                        }
                                    }}
                                    size="small"
                                    style={{ backgroundColor: '#fff' }}
                                />
                                <IconButton
                                    color="primary"
                                    onClick={handleSendMessage}
                                    disabled={!chatInput.trim()}
                                >
                                    <SendIcon />
                                </IconButton>
                            </Box>
                        )}
                    </Paper>
            </Box>

            <Dialog
                open={composeOpen}
                onClose={() => {
                    setComposeOpen(false);
                    setComposeStudent(null);
                    composeFormik.resetForm();
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle style={{ fontWeight: 800 }}>
                    {composeStudent || selectedStudent
                        ? `Write a message — ${getStudentName(composeStudent || selectedStudent)}`
                        : 'Write a message'}
                </DialogTitle>
                <DialogContent>
                    <Card className={classes.composeCard}>
                        <CardContent>
                            <form id="note-list-compose-form" onSubmit={composeFormik.handleSubmit}>
                                <Box style={{ marginBottom: 16 }}>
                                    <FormLabel className={classes.sectionTitle} style={{ marginBottom: 8 }}>
                                        Message*
                                    </FormLabel>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={4}
                                        name="note"
                                        value={composeFormik.values.note}
                                        onChange={composeFormik.handleChange}
                                        onBlur={composeFormik.handleBlur}
                                        placeholder="Enter your message here..."
                                        variant="outlined"
                                        style={{ marginTop: 8 }}
                                        error={Boolean(composeFormik.touched.note && composeFormik.errors.note)}
                                        helperText={
                                            composeFormik.touched.note && composeFormik.errors.note
                                                ? composeFormik.errors.note
                                                : ''
                                        }
                                    />
                                </Box>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    className={classes.yellowButton}
                                    disabled={
                                        !String(composeFormik.values.note || '').trim() ||
                                        composeFormik.isSubmitting
                                    }
                                >
                                    SEND
                                </Button>
                            </form>
                        </CardContent>
                    </Card>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setComposeOpen(false);
                            setComposeStudent(null);
                            composeFormik.resetForm();
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={toast.open}
                autoHideDuration={3000}
                onClose={() => setToast((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <SnackbarContent
                    style={{
                        backgroundColor: toast.type === 'success' ? '#2e7d32' : '#d32f2f',
                        color: '#fff',
                        fontWeight: 600,
                    }}
                    message={toast.message}
                />
            </Snackbar>
        </>
    );
}
